import { useEffect } from 'react';
import { loadPublicSettings, savePublicSettings } from '../lib/publicSettings';
import { isProduction } from '../lib/growthIntel/env';
import { setExcludedHosts } from '../lib/growthIntel/env';
import { setConsentMode, getConsentMode, thirdPartyAllowed } from '../lib/growthIntel/consent';

const GA4_KEY = 'cendien_ga4_id';
const GSC_KEY = 'cendien_gsc_verification';
const BING_KEY = 'cendien_bing_verification';
const GTM_KEY = 'cendien_gtm_id';
const LINKEDIN_KEY = 'cendien_linkedin_partner_id';
const CLARITY_KEY = 'cendien_clarity_id';
const META_PIXEL_KEY = 'cendien_meta_pixel_id';
const TEAMS_WEBHOOK_KEY = 'cendien_teams_webhook';

export interface IntegrationsConfig {
  ga4Id: string;
  gscVerification: string;
  bingVerification: string;
  gtmId: string;
  linkedinPartnerId: string;
  clarityId: string;
  metaPixelId: string;
  teamsWebhook: string;
}

/** Reads the locally cached integration config (used for instant admin UI). */
export function getGoogleIntegrations(): IntegrationsConfig {
  return {
    ga4Id: localStorage.getItem(GA4_KEY) ?? '',
    gscVerification: localStorage.getItem(GSC_KEY) ?? '',
    bingVerification: localStorage.getItem(BING_KEY) ?? '',
    gtmId: localStorage.getItem(GTM_KEY) ?? '',
    linkedinPartnerId: localStorage.getItem(LINKEDIN_KEY) ?? '',
    clarityId: localStorage.getItem(CLARITY_KEY) ?? '',
    metaPixelId: localStorage.getItem(META_PIXEL_KEY) ?? '',
    teamsWebhook: localStorage.getItem(TEAMS_WEBHOOK_KEY) ?? '',
  };
}

/**
 * Saves the integration config. Analytics/verification settings are
 * persisted to `cms_content.public_settings` (publicly readable) so every
 * visitor can load them — localStorage is only a local cache.
 */
export function saveGoogleIntegrations(config: Partial<IntegrationsConfig>) {
  applyLocalConfig(config);
  // Fire-and-forget persist to the public settings row
  savePublicSettings(config).catch(() => {
    // non-fatal — the local cache still holds the values for this session
  });
}

/** Writes config to localStorage only (no network). */
function applyLocalConfig(config: Partial<IntegrationsConfig>) {
  const entries: [string, string | undefined][] = [
    [GA4_KEY, config.ga4Id],
    [GSC_KEY, config.gscVerification],
    [BING_KEY, config.bingVerification],
    [GTM_KEY, config.gtmId],
    [LINKEDIN_KEY, config.linkedinPartnerId],
    [CLARITY_KEY, config.clarityId],
    [META_PIXEL_KEY, config.metaPixelId],
    [TEAMS_WEBHOOK_KEY, config.teamsWebhook],
  ];
  entries.forEach(([key, value]) => {
    if (value === undefined) return;
    if (value.trim()) {
      localStorage.setItem(key, value.trim());
    } else {
      localStorage.removeItem(key);
    }
  });
}

/**
 * Loads the integration config from `cms_content.public_settings` and hydrates
 * the local cache. Returns the loaded partial config.
 */
export async function loadIntegrationsFromSupabase(): Promise<Partial<IntegrationsConfig>> {
  const settings = await loadPublicSettings();
  const config: Partial<IntegrationsConfig> = {
    ga4Id: settings.ga4Id ?? '',
    gscVerification: settings.gscVerification ?? '',
    bingVerification: settings.bingVerification ?? '',
    gtmId: settings.gtmId ?? '',
    linkedinPartnerId: settings.linkedinPartnerId ?? '',
    clarityId: settings.clarityId ?? '',
    metaPixelId: settings.metaPixelId ?? '',
    teamsWebhook: settings.teamsWebhook ?? '',
  };
  if (Object.values(config).some((v) => v)) applyLocalConfig(config);
  return config;
}

/** Hook: hydrates the local config from the cloud on mount (admin UI). */
export function useIntegrationsSync(
  onLoaded?: (config: Partial<IntegrationsConfig>) => void,
) {
  useEffect(() => {
    loadIntegrationsFromSupabase().then((config) => {
      onLoaded?.(config);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Teams notifications are now sent server-side by the `submit-form` edge
 * function. This client helper remains for the admin "Test Webhook" button.
 */
export async function sendTeamsNotification(
  title: string,
  message: string,
  facts?: { name: string; value: string }[],
) {
  const { teamsWebhook } = getGoogleIntegrations();
  if (!teamsWebhook) return;

  const body: Record<string, unknown> = {
    '@type': 'MessageCard',
    '@context': 'https://schema.org/extensions',
    summary: title,
    themeColor: '14B8A6',
    title,
    sections: [
      {
        text: message,
        ...(facts && facts.length > 0 ? { facts } : {}),
      },
    ],
  };

  try {
    await fetch(teamsWebhook, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // Silently fail — tracking should never break the app
  }
}

/** Sets Google Consent Mode v2 defaults (denied) before GTM loads, then updates. */
function applyGoogleConsentDefaults() {
  if (getConsentMode() !== 'opt_in') return;
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(['consent', 'default', {
    ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied',
    analytics_storage: 'denied', functionality_storage: 'granted', security_storage: 'granted',
  }]);
  w.dataLayer.push(['consent', 'update', {
    ad_storage: 'granted', ad_user_data: 'granted', ad_personalization: 'granted', analytics_storage: 'granted',
  }]);
}

/** Injects the analytics/tracking scripts from a resolved config. */
function injectTrackingScripts(cfg: IntegrationsConfig) {
  const { ga4Id, gscVerification, bingVerification, gtmId, linkedinPartnerId, clarityId, metaPixelId } = cfg;

  // Consent Mode v2 defaults must be pushed BEFORE GTM loads.
  if (gtmId || ga4Id) applyGoogleConsentDefaults();

  // ── Google Tag Manager ────────────────────────────────────────
  if (gtmId && !document.getElementById('gtm-script')) {
    const gtmScript = document.createElement('script');
    gtmScript.id = 'gtm-script';
    gtmScript.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.insertBefore(gtmScript, document.head.firstChild);

    if (!document.getElementById('gtm-noscript')) {
      const noscript = document.createElement('noscript');
      noscript.id = 'gtm-noscript';
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      noscript.appendChild(iframe);
      if (document.body) {
        if (document.body.firstChild) {
          document.body.insertBefore(noscript, document.body.firstChild);
        } else {
          document.body.appendChild(noscript);
        }
      }
    }
  }

  // ── Google Analytics 4 (only if GTM not managing it) ─────────
  if (ga4Id && !gtmId && !document.getElementById('ga4-script')) {
    const script1 = document.createElement('script');
    script1.id = 'ga4-script';
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.id = 'ga4-init';
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${ga4Id}', { send_page_view: true });
    `;
    document.head.appendChild(script2);
  }

  // ── Google Search Console verification meta tag ───────────────
  if (gscVerification && !document.getElementById('gsc-meta')) {
    const meta = document.createElement('meta');
    meta.id = 'gsc-meta';
    meta.name = 'google-site-verification';
    meta.content = gscVerification;
    document.head.appendChild(meta);
  }

  // ── Bing Webmaster verification meta tag ──────────────────────
  if (bingVerification && !document.getElementById('bing-meta')) {
    const meta = document.createElement('meta');
    meta.id = 'bing-meta';
    meta.name = 'msvalidate.01';
    meta.content = bingVerification;
    document.head.appendChild(meta);
  }

  // ── LinkedIn Insight Tag ──────────────────────────────────────
  if (linkedinPartnerId && !document.getElementById('linkedin-insight')) {
    const initScript = document.createElement('script');
    initScript.id = 'linkedin-insight';
    initScript.innerHTML = `
      _linkedin_partner_id = "${linkedinPartnerId}";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    `;
    document.head.appendChild(initScript);

    const loaderScript = document.createElement('script');
    loaderScript.id = 'linkedin-insight-loader';
    loaderScript.async = true;
    loaderScript.innerHTML = `
      (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);
      })(window.lintrk);
    `;
    document.head.appendChild(loaderScript);
  }

  // ── Microsoft Clarity ─────────────────────────────────────────
  if (clarityId && !document.getElementById('clarity-script')) {
    const clarityScript = document.createElement('script');
    clarityScript.id = 'clarity-script';
    clarityScript.type = 'text/javascript';
    clarityScript.innerHTML = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", "${clarityId}");
    `;
    document.head.appendChild(clarityScript);
  }

  // ── Meta Pixel ────────────────────────────────────────────────
  if (metaPixelId && !document.getElementById('meta-pixel-script')) {
    const pixelScript = document.createElement('script');
    pixelScript.id = 'meta-pixel-script';
    pixelScript.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${metaPixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(pixelScript);
  }
}

/**
 * Loads analytics from `cms_content.public_settings` for EVERY visitor and
 * injects the tracking scripts. Falls back to the local cache if the fetch
 * fails so an already-configured site never goes dark.
 */
export function useGoogleIntegrations() {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Hydrate consent mode + host filters from public settings first.
      let settings: Awaited<ReturnType<typeof loadPublicSettings>> = {};
      try { settings = await loadPublicSettings(); } catch { /* keep defaults */ }
      setConsentMode(settings.giConsentMode);
      setExcludedHosts(settings.giExcludedHosts);

      // HARD GUARD: analytics NEVER loads off the production host, and never
      // loads in opt-in mode before consent (or when GPC is set).
      if (cancelled || !isProduction()) return;
      if (!thirdPartyAllowed()) return;

      let cfg = getGoogleIntegrations();
      try {
        cfg = {
          ga4Id: settings.ga4Id ?? cfg.ga4Id,
          gscVerification: settings.gscVerification ?? cfg.gscVerification,
          bingVerification: settings.bingVerification ?? cfg.bingVerification,
          gtmId: settings.gtmId ?? cfg.gtmId,
          linkedinPartnerId: settings.linkedinPartnerId ?? cfg.linkedinPartnerId,
          clarityId: settings.clarityId ?? cfg.clarityId,
          metaPixelId: settings.metaPixelId ?? cfg.metaPixelId,
          teamsWebhook: settings.teamsWebhook ?? cfg.teamsWebhook,
        };
      } catch {
        // keep local cache
      }
      if (!cancelled) injectTrackingScripts(cfg);
    })();
    return () => { cancelled = true; };
  }, []);
}